import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from 'aws-blocks';
import { GAME_MODES, getLevelFromXP } from '@blind-kings/game-core';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useSnackBarContext } from '../../../contexts/SnackBarContext';
import { resolveTeamPalettes } from '../../../player-colors';
import { preloadMatchAssets } from '../../Game/pixi';
import type { MatchOpponent } from '../MatchReveal';

export type QueueStatus = {
  state: 'idle' | 'searching' | 'matched';
  gameMode?: string;
  gameId?: string;
  joinedAt?: number;
  onlineCount?: number;
  gamesInProgress?: number;
};

const VALID_MODES = new Set([GAME_MODES.ONLINE_GAME, GAME_MODES.RANKED_GAME]);
const POLL_MS = 2000;
const REVEAL_MS = 5000;

type SeatLike = {
  playerId?: string;
  displayName?: string;
  avatarId?: string;
  colorId?: string;
  displayFrameId?: string;
  xp?: number;
  wins?: number;
  losses?: number;
  elo?: number;
  tier?: string;
  rankedWins?: number;
  rankedLosses?: number;
};

function seatToOpponent(seat: SeatLike | undefined): MatchOpponent {
  const xp = Number(seat?.xp ?? 0);
  return {
    displayName: seat?.displayName,
    avatarId: seat?.avatarId,
    displayFrameId: seat?.displayFrameId,
    xp,
    wins: Number(seat?.wins ?? 0),
    losses: Number(seat?.losses ?? 0),
    level: getLevelFromXP(xp),
    elo: seat?.elo != null ? Number(seat.elo) : undefined,
    tier: seat?.tier ? String(seat.tier) : undefined,
    rankedWins:
      seat?.rankedWins != null ? Number(seat.rankedWins) : undefined,
    rankedLosses:
      seat?.rankedLosses != null ? Number(seat.rankedLosses) : undefined,
  };
}

export default function useQueue() {
  const { mode } = useParams();
  const navigate = useNavigate();
  const { currentUser, userData, openLoginModal, queryUserData } = useAuthContext();
  const { addSnackBar } = useSnackBarContext();
  const [status, setStatus] = useState<QueueStatus>({
    state: 'searching',
    onlineCount: 0,
    gamesInProgress: 0,
  });
  const [joined, setJoined] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [reveal, setReveal] = useState<{
    gameId: string;
    you: MatchOpponent;
    opponent: MatchOpponent;
    secondsLeft: number;
  } | null>(null);
  const leavingRef = useRef(false);
  const navigatedRef = useRef(false);
  const revealStartedRef = useRef(false);
  const gameMode = VALID_MODES.has(String(mode)) ? String(mode) : undefined;

  useEffect(() => {
    if (!gameMode) {
      navigate('/', { replace: true });
      return;
    }
    if (!currentUser) {
      openLoginModal();
      navigate('/', { replace: true });
    }
  }, [gameMode, currentUser, navigate, openLoginModal]);

  async function beginReveal(gameId: string) {
    if (revealStartedRef.current || navigatedRef.current) return;
    revealStartedRef.current = true;
    leavingRef.current = true;

    try {
      await queryUserData();
      const game = (await api.getGame(gameId)) as {
        firstPlayer?: SeatLike;
        secondPlayer?: SeatLike;
        startedAt?: number;
        createdAt?: number;
      };

      // Both armies are known now, so spend the reveal downloading their strips.
      void preloadMatchAssets(
        resolveTeamPalettes(
          game.firstPlayer?.colorId,
          game.secondPlayer?.colorId
        )
      ).catch(() => undefined);

      // Resuming an in-progress match — skip the versus interstitial.
      const startedAt = Number(game.startedAt ?? game.createdAt ?? 0);
      if (startedAt && Date.now() - startedAt > 12_000) {
        navigatedRef.current = true;
        navigate(`/play/${gameId}`, { replace: true });
        return;
      }

      const myId = String(userData?.playerId ?? currentUser?.userId ?? '');
      const first = game.firstPlayer;
      const second = game.secondPlayer;
      const youSeat =
        String(first?.playerId) === myId
          ? first
          : String(second?.playerId) === myId
            ? second
            : first;
      const oppSeat =
        String(first?.playerId) === myId
          ? second
          : String(second?.playerId) === myId
            ? first
            : second;

      const youFromAuth: MatchOpponent = {
        displayName: userData?.displayName ?? youSeat?.displayName,
        avatarId: userData?.avatarId ?? youSeat?.avatarId,
        displayFrameId:
          (userData?.ranked?.displayFrameId as string | undefined) ??
          youSeat?.displayFrameId,
        xp: Number(userData?.xp ?? youSeat?.xp ?? 0),
        wins: Number(userData?.wins ?? youSeat?.wins ?? 0),
        losses: Number(userData?.losses ?? youSeat?.losses ?? 0),
        level: getLevelFromXP(Number(userData?.xp ?? youSeat?.xp ?? 0)),
        elo:
          userData?.ranked?.elo != null
            ? Number(userData.ranked.elo)
            : youSeat?.elo != null
              ? Number(youSeat.elo)
              : undefined,
        tier:
          (userData?.ranked?.tier as string | undefined) ??
          (youSeat?.tier ? String(youSeat.tier) : undefined),
        rankedWins:
          userData?.ranked?.wins != null
            ? Number(userData.ranked.wins)
            : youSeat?.rankedWins != null
              ? Number(youSeat.rankedWins)
              : undefined,
        rankedLosses:
          userData?.ranked?.losses != null
            ? Number(userData.ranked.losses)
            : youSeat?.rankedLosses != null
              ? Number(youSeat.rankedLosses)
              : undefined,
      };

      setReveal({
        gameId,
        you: youFromAuth,
        opponent: seatToOpponent(oppSeat),
        secondsLeft: Math.ceil(REVEAL_MS / 1000),
      });
    } catch {
      navigatedRef.current = true;
      navigate(`/play/${gameId}`, { replace: true });
    }
  }

  useEffect(() => {
    if (!currentUser || !gameMode || leavingRef.current) return;
    let cancelled = false;

    (async () => {
      try {
        const result = (await api.joinQueue(gameMode)) as QueueStatus;
        if (cancelled) return;
        setStatus(result);
        setJoined(true);
        if (result.state === 'matched' && result.gameId) {
          void beginReveal(result.gameId);
        }
      } catch (err) {
        if (cancelled) return;
        addSnackBar({
          title: 'Queue Error',
          message: err instanceof Error ? err.message : String(err),
          variant: 'danger',
          timeout: 2500,
        });
        navigate('/', { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, gameMode, navigate, addSnackBar]);

  useEffect(() => {
    if (!joined || !gameMode || leavingRef.current || revealStartedRef.current) return;

    let cancelled = false;
    const poll = async () => {
      if (cancelled || document.visibilityState === 'hidden') return;
      try {
        const next = (await api.getQueueStatus()) as QueueStatus;
        if (cancelled) return;
        setStatus(next);
        if (next.state === 'matched' && next.gameId) {
          void beginReveal(next.gameId);
        }
      } catch {
        // retry next tick
      }
    };

    void poll();
    const id = setInterval(() => void poll(), POLL_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void poll();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joined, gameMode, navigate]);

  useEffect(() => {
    const joinedAt = status.joinedAt;
    if (!joinedAt || status.state !== 'searching' || reveal) return;
    const tick = () =>
      setElapsedSec(Math.max(0, Math.floor((Date.now() - joinedAt) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [status.joinedAt, status.state, reveal]);

  useEffect(() => {
    if (!reveal) return;
    const started = Date.now();
    const tick = () => {
      const left = Math.ceil((REVEAL_MS - (Date.now() - started)) / 1000);
      if (left <= 0) {
        if (!navigatedRef.current) {
          navigatedRef.current = true;
          navigate(`/play/${reveal.gameId}`, { replace: true });
        }
        return;
      }
      setReveal((prev) => (prev ? { ...prev, secondsLeft: left } : prev));
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [reveal?.gameId, navigate]);

  async function cancelQueue() {
    if (revealStartedRef.current) return;
    leavingRef.current = true;
    try {
      await api.leaveQueue();
    } catch {
      // ignore
    }
    navigate('/');
  }

  useEffect(() => {
    return () => {
      if (leavingRef.current || navigatedRef.current || revealStartedRef.current) return;
      void api.leaveQueue().catch(() => undefined);
    };
  }, []);

  const modeLabel =
    gameMode === GAME_MODES.RANKED_GAME ? 'Ranked' : 'Online';

  return {
    gameMode,
    modeLabel,
    status,
    elapsedSec,
    cancelQueue,
    reveal,
  };
}
